import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider, SubmitErrorHandler } from 'react-hook-form';
import { ButtonFooter, CreatableSelect, DatePicker, FormLayout, TextInput } from '~/components';
import { OptionType } from '~/features/types';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { FormRow as Row } from '../../../components/forms/FormRow';
import { FormSection as Section } from '../../../components/forms/FormSection';
import { CERTIFICATES_OPTIONS } from './constants';
import { certSchema } from './schemas';
import { CertFormValues } from './types';
interface FormProps {
  handleClose: () => void;
  createCert: (values: CertFormValues) => void;
  editCert: (values: CertFormValues) => void;
  selectCertName: (certName: string) => void;
  form: Partial<CertFormValues>;
  edit: boolean;
  issuerOptions: OptionType[];
}
const TAGS = CT.certs.form;

const VALID_FIELDS: (keyof CertFormValues)[] = ['expiryDate', 'issueDate'];

export const CertForm = (props: FormProps) => {
  const { handleClose, createCert, editCert, selectCertName, form, edit, issuerOptions } = props;

  const methods = useCustomForm<CertFormValues>({
    resolver: zodResolver(certSchema),
    defaultValues: form,
  });

  useServerWarnings(methods, form.warnings, VALID_FIELDS);

  const { handleSubmit, watch, setValue } = methods;

  const onSubmitSuccess = (values: CertFormValues) => {
    if (edit) {
      editCert(values);
    } else {
      createCert(values);
    }
  };

  const onSubmitError: SubmitErrorHandler<CertFormValues> = (error) => {
    console.debug('Certform Error: ', error);
    console.log(error);
  };
  const certNameValue = watch('certName');

  useEffect(() => {
    if (certNameValue) {
      selectCertName(certNameValue.value);
    }
  }, [certNameValue]);

  // Reset issuer when issuerOptions change if current selection isn't valid
  useEffect(() => {
    const currentIssuer = methods.getValues('issuer');
    if (currentIssuer && issuerOptions.length > 0) {
      const isValidIssuer = issuerOptions.some((opt) => opt.value === currentIssuer.value);
      if (!isValidIssuer) {
        setValue('issuer', issuerOptions[0]);
      }
    }
  }, [issuerOptions, setValue]);

  return (
    <FormProvider {...methods}>
      <form
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="board-cert-form"
      >
        <FormLayout className="bg-white">
          <Section componentsPerLine={1}>
            <Row>
              {/* [MCV-1721] certSchema enforces both selects (MCV-1718: a marker
                  never claims more than validation enforces). */}
              <CreatableSelect
                label="Type of Certification"
                options={CERTIFICATES_OPTIONS}
                name={'certName'}
                cyTag={TAGS.certName}
                required
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <CreatableSelect
                name={'issuer'}
                label={'Issuer'}
                cyTag={TAGS.issuer}
                options={issuerOptions}
                required
              />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <TextInput name="certNum" cyTag={TAGS.certNum} label={'Certificate Number'} />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            {/* [MCV-1066] Both dates stay optional in certSchema -- a provider
                may not have them to hand -- but a certification without them
                cannot be checked for currency, so nudge rather than require. */}
            <Row>
              <DatePicker
                name={'issueDate'}
                label={'Issue Date'}
                cyTag={TAGS.issueDate}
                recommended="Adding this helps us keep your certification current."
              />
            </Row>
            <Row>
              <DatePicker
                name={'expiryDate'}
                label={'Expired Date'}
                cyTag={TAGS.expiryDate}
                recommended="Adding this lets us remind you before it expires."
              />
            </Row>
          </Section>
          <ButtonFooter
            primaryButton={{
              label: 'Save',
              cyTag: TAGS.submitBtn,
              isSubmit: true,
            }}
            secondaryButton={{
              label: 'Cancel',
              cyTag: TAGS.cancelBtn,
              onClick: handleClose,
            }}
          />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
